import { useState } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../services/firebase";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");

  const navigate = useNavigate();

  const handleLogin = async () => {
    setError("");
    setMessage("");
    setLoading(true);

    try {
      const userCred = await signInWithEmailAndPassword(auth, email, password);

      if (!userCred.user.emailVerified) {
        setError("Please verify your email before login.");
        setLoading(false);
        return;
      }

      navigate("/home");
    } catch (err) {
      setError("Invalid email or password");
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (loading) return;

    setError("");
    setMessage("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!name.trim() || !company.trim()) {
      setError("Please fill all fields");
      return;
    }

    setLoading(true);

    try {
      // ✅ 1. Create auth user
      const userCred = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );

      const user = userCred.user;

      // ✅ 2. Save user profile
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        name: name.trim(),
        company: company.trim(),
        email: email,
        createdAt: serverTimestamp(),
      });

      // ✅ 3. Send verification email (IMPORTANT FIX)
      await sendEmailVerification(auth.currentUser);

      // ✅ success UI
      setMessage("Verification email sent. Please check your inbox or spam folder.");

      // reset form
      setIsRegister(false);
      setName("");
      setCompany("");
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("REGISTER ERROR:", err);
      setError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Enter your email first");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("Password reset email sent.");
      setError("");
    } catch (err) {
      setError("Failed to send reset email");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;

    isRegister ? handleRegister() : handleLogin();
  };

  return (
    <div style={styles.container}>
      <form
        style={{
          ...styles.card,
          width: isRegister ? "720px" : "420px",
        }}
        onSubmit={handleSubmit}
      >
        <h1 style={styles.title}>
          {isRegister ? "Create Account" : "Invoice Generator"}
        </h1>

        <p style={styles.subtitle}>
          {isRegister
            ? "Join us to create professional invoices and manage your business seamlessly."
            : "Create, manage, and track invoices & quotations in one powerful dashboard."}
        </p>

        {/* REGISTER GRID ROW 1 */}
        {isRegister && (
          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Full Name</label>
              <input
                type="text"
                style={styles.input}
                placeholder="Enter your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Company</label>
              <input
                type="text"
                style={styles.input}
                placeholder="Enter company name"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                disabled={loading}
                required
              />
            </div>
          </div>
        )}

        {/* REGISTER ROW 2 */}
        {isRegister ? (
          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                style={styles.input}
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword ? "text" : "password"}
                  style={styles.passwordInput}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                />
                <span
                  style={styles.iconWrapper}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* LOGIN EMAIL */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                style={styles.input}
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            {/* LOGIN PASSWORD */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword ? "text" : "password"}
                  style={styles.passwordInput}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                />
                <span
                  style={styles.iconWrapper}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </span>
              </div>
            </div>
          </>
        )}

        {/* REGISTER ROW 3 */}
        {isRegister && (
          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Confirm Password</label>
              <input
                type="password"
                style={styles.input}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                required
              />
            </div>
          </div>
        )}

        {/* Forgot password */}
        {!isRegister && (
          <p style={styles.forgot} onClick={handleForgotPassword}>
            Forgot password?
          </p>
        )}

        {error && <p style={styles.error}>{error}</p>}
        {message && <p style={styles.success}>{message}</p>}

        <button type="submit" style={styles.button} disabled={loading}>
          {loading ? "Please wait..." : isRegister ? "Sign Up" : "Sign In"}
        </button>

        <p style={styles.toggleText}>
          {isRegister ? "Already have an account?" : "Don't have an account?"}
          <span
            style={styles.toggleLink}
            onClick={() => {
              setIsRegister(!isRegister);
              setError("");
              setMessage("");
            }}
          >
            {isRegister ? " Sign In" : " Sign Up"}
          </span>
        </p>
      </form>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background:
      "linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #2563eb 100%)",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    marginBottom: "4px",
  },

  card: {
    padding: "20px 40px",
    borderRadius: "16px",
    background: "#fff",
    transition: "width 0.25s ease",
  },

  title: {
    textAlign: "center",
    marginBottom: 20,
    color: "#334155",
    fontSize: 25,
  },
  subtitle: {
    textAlign: "center",
    color: "#64748b",
    fontSize: "14px",
    marginTop: "-10px",
    marginBottom: "14px",
    lineHeight: 1.5,
  },
  inputGroup: { marginBottom: 16 },
  label: {
    display: "block",
    marginBottom: 6,
    color: "#334155",
    fontWeight: 600,
  },
  input: {
    width: "100%",
    padding: 10,
    borderRadius: 6,
    border: "1px solid #ddd",
  },
  passwordWrapper: { position: "relative" },
  passwordInput: {
    width: "100%",
    padding: "10px 40px 10px 10px",
    borderRadius: 6,
    border: "1px solid #ddd",
  },
  iconWrapper: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    cursor: "pointer",
  },
  forgot: {
    textAlign: "right",
    fontSize: 13,
    color: "#2563eb",
    cursor: "pointer",
    marginBottom: 10,
  },
  button: {
    width: "100%",
    padding: 12,
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontWeight: "bold",
  },
  error: { color: "red", textAlign: "center" },
  success: { color: "green", textAlign: "center" },
  toggleText: { textAlign: "center", marginTop: 16 },
  toggleLink: {
    color: "#2563eb",
    cursor: "pointer",
    fontWeight: "bold",
    marginLeft: 5,
  },
};

export default Login;
